import { type CSSProperties, type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { useKeyboardNavHighlight } from '../../hooks/useKeyboardNavHighlight';
import { useResponsivePanelPlacement } from '../../hooks/useResponsivePanelPlacement';
import { splitOnMatch } from '../../lib/split-on-match';
import { Input } from './Input';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';

/**
 * Portal-based searchable popover anchored to a trigger button.
 *
 * Lives in its own module (rather than inline in emails.tsx) so that components
 * outside the route - e.g. `InlineMatchChips` under `components/email/` - can
 * import it without creating a route <-> component import cycle.
 *
 * Features:
 * - Portals to document.body so it escapes any dialog / overflow clipping
 * - Auto-focuses the search input on mount
 * - Closes on outside click or Escape
 * - Renders a paginated list with optional "Load more" footer
 * - Optional `pinnedAction` prop: a primary-green two-line button pinned ABOVE
 *   the results list, always visible regardless of search/loading/empty state.
 *   Use for "Create …" shortcuts that should always be reachable.
 * - Keyboard navigation: ArrowDown/ArrowUp move a managed highlight, Enter commits
 *   the highlighted row (or the first row when nothing is highlighted). Adopt
 *   `useKeyboardNavHighlight` so the fixed-position panel never jumps on scroll and
 *   a resting cursor cannot steal the keyboard highlight.
 * - Optional extensions, all off by default: grouped rows (`item.group`), row
 *   icons, a pinned action that joins the arrow cycle as the first option
 *   (`pinnedAction.inKeyboardCycle`), accessible names for the input and list,
 *   a leading input icon, and a taller desktop list.
 * - Opt-in header-field presentation (`appearance="rich"`, `panelAlign`,
 *   `panelGapPx`, `scrimTopPx`, `searchInputHost`): icon-tile rows with the typed
 *   text emphasised and trailing content, a panel centred on its trigger, a light
 *   scrim, and the search input hosted in the caller's own field instead of the
 *   panel. Desktop only for the input host and scrim; the phone sheet is unchanged.
 * - Escape closes and returns focus to the trigger. An outside click does not
 *   move focus: the user chose to go elsewhere.
 *
 * Responsive placement (so the panel is never stranded off-screen):
 * - Desktop (viewport >= 640px): anchored to the trigger and follows
 *   scroll/resize. The panel flips ABOVE the trigger when there is not enough
 *   room below, and its inner list is capped to the space actually available on
 *   the chosen side - it scrolls internally rather than spilling past the
 *   viewport edge. A trigger on the last row of a long table stays usable.
 * - Mobile (viewport < 640px): renders as a bottom sheet docked to the foot of
 *   the screen with a dimmed backdrop, search pinned on top, and the results
 *   scrolling beneath. Anchoring a dropdown to a table row is unworkable on a
 *   phone, so we drop it there. Focus is trapped within the sheet.
 *
 * Both branches' panel carry `data-popover-panel`, so a `Popover` (or another
 * `SearchPopover`) hosting this one as a nested picker can recognise a click
 * inside it as "inside" for its own outside-click dismissal, even though this
 * panel portals to its own sibling of `document.body` rather than a DOM
 * descendant of the hosting panel.
 */

export interface SearchPopoverItem {
	id: string;
	label: string;
	sublabel?: string;
	/**
	 * Consecutive items sharing a `group` render under one visible heading, as a
	 * `role="group"` labelled by it. Headings are never options. Items without a
	 * group render flat.
	 */
	group?: string;
	/** Decorative leading icon; the caller marks it `aria-hidden`. */
	icon?: ReactNode;
	/** Content pinned to the row's right edge (`appearance="rich"` only), e.g. an amount and a status badge. */
	trailing?: ReactNode;
	/**
	 * Choosing the row calls `onSelect` but leaves the popover open, for a row
	 * that widens or narrows the list in place (a collapsed group's toggle).
	 */
	keepOpenOnSelect?: boolean;
}

export interface SearchPopoverPinnedAction {
	/** First line – the action label, e.g. '+ Create expense "Eskom"'. */
	label: string;
	/** Optional second line – context, e.g. 'Pre-filled with R1,250.00 on 3 Jun 2026'. */
	sublabel?: string;
	onSelect: () => void;
	/**
	 * Keep the popover open after `onSelect` fires. Default behaviour (and every
	 * existing caller) closes the popover, which suits a "Create …" shortcut
	 * that hands off to a new form. Set this for a toggle that widens or
	 * narrows the same list in place, where closing would force the operator
	 * to reopen the popover just to see the result.
	 */
	keepOpenOnSelect?: boolean;
	/**
	 * Render the action as the first option inside the listbox instead of above
	 * it: ArrowUp/ArrowDown reach it, it is highlighted whenever the item set
	 * changes, and Enter commits it when no other row is highlighted. Items then
	 * occupy option indices 1..n.
	 */
	inKeyboardCycle?: boolean;
	/** Decorative icon in the action's tile (`appearance="rich"` only); the caller marks it `aria-hidden`. */
	icon?: ReactNode;
	/** Short word beside an Enter key glyph while the action is highlighted (`appearance="rich"` only), e.g. "Ask". */
	enterHint?: string;
}

export interface SearchPopoverProps {
	triggerRef: React.RefObject<HTMLButtonElement | null>;
	onClose: () => void;
	searchValue: string;
	onSearchChange: (value: string) => void;
	items: SearchPopoverItem[];
	onSelect: (id: string) => void;
	selectedId?: string;
	emptyLabel?: string;
	placeholder?: string;
	hasMore?: boolean;
	onLoadMore?: () => void;
	isLoadingMore?: boolean;
	isLoading?: boolean;
	/**
	 * Tailwind-compatible min-width / max-width pair for the popover. Defaults
	 * to a chip-sized popover; pickers with longer labels (invoice number +
	 * client name + amount) pass a wider value.
	 */
	widthClass?: string;
	widthPx?: number;
	/**
	 * Where the popover anchors relative to the trigger (desktop only - on
	 * mobile the panel is always a bottom sheet).
	 * - 'below' (default): popover top sits below trigger bottom, both visible.
	 *   Right choice for chip / pill triggers, where the trigger is a small
	 *   element separate from the search.
	 * - 'overlay': popover top aligns with trigger top, search input replaces
	 *   the trigger visually. The consumer should hide its trigger while
	 *   the popover is open. Right choice for full-width input-style triggers
	 *   that would otherwise read as a second search field stacked above the
	 *   popover's own search input.
	 *
	 * When room below is tight the panel flips above the trigger regardless of
	 * this mode; the mode only sets the preferred (downward) anchor.
	 */
	anchorMode?: 'below' | 'overlay';
	/**
	 * Optional action pinned ABOVE the results list. Always visible regardless
	 * of search/loading/empty state. Styled as a distinct primary-green two-line
	 * button, visually separated from the results by a hairline divider.
	 *
	 * Existing callers that do not pass this prop are unaffected.
	 *
	 * Use for "Create …" shortcuts that should always be reachable even when
	 * the search returns no matching records.
	 */
	pinnedAction?: SearchPopoverPinnedAction;
	/** Accessible name for the search input (otherwise only the placeholder names it). */
	inputAriaLabel?: string;
	/** Accessible name for the results listbox. */
	listAriaLabel?: string;
	/** Decorative icon inside the left edge of the search input; the caller marks it `aria-hidden`. */
	inputLeadingIcon?: ReactNode;
	/** Tailwind max-height class for the desktop list. Defaults to `max-h-[240px]`. */
	listMaxHeightClass?: string;
	/**
	 * `'default'`: compact text rows. `'rich'`: rounded rows with a 32px icon tile,
	 * the typed text emphasised in the label and sublabel, `item.trailing` on the
	 * right, and a tinted pinned action.
	 */
	appearance?: 'default' | 'rich';
	/** Desktop 'below' mode: centre the panel on the trigger instead of left-aligning it. */
	panelAlign?: 'start' | 'center';
	/** Desktop 'below' mode: pixels between trigger and panel. Defaults to 4. */
	panelGapPx?: number;
	/**
	 * Desktop only: dim everything from this many pixels down the viewport behind
	 * the panel, so a header above that line stays clear. A press on it closes the
	 * popover.
	 */
	scrimTopPx?: number;
	/**
	 * Desktop only: render the search input inside this element (the caller's own
	 * field) instead of at the top of the panel. The input stays part of this
	 * component's React tree, so its keyboard handling and ids are unchanged, and
	 * a press inside the host does not count as an outside click. The caller
	 * supplies the element once it exists; until then no input is rendered.
	 */
	searchInputHost?: HTMLElement | null;
}

export function SearchPopover({
	triggerRef,
	onClose,
	searchValue,
	onSearchChange,
	items,
	onSelect,
	selectedId,
	emptyLabel = 'No results found',
	placeholder = 'Search...',
	hasMore,
	onLoadMore,
	isLoadingMore,
	isLoading,
	widthClass = 'min-w-[280px] max-w-[320px]',
	widthPx = 320,
	anchorMode = 'below',
	pinnedAction,
	inputAriaLabel,
	listAriaLabel,
	inputLeadingIcon,
	listMaxHeightClass = 'max-h-[240px]',
	appearance = 'default',
	panelAlign = 'start',
	panelGapPx,
	scrimTopPx,
	searchInputHost,
}: SearchPopoverProps) {
	const rich = appearance === 'rich';
	const popoverRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const listRef = useRef<HTMLUListElement>(null);

	// ---------------------------------------------------------------------------
	// Keyboard navigation state
	// ---------------------------------------------------------------------------
	// -1 = no row highlighted. The range is 0 … optionCount - 1, where the
	// options are the items, preceded by the pinned action when it opts in via
	// `inKeyboardCycle`. Otherwise the pinned action and the Load-more row stay
	// mouse/Tab reachable but are NOT part of the arrow cycle.
	const pinnedInCycle = Boolean(pinnedAction?.inKeyboardCycle);
	const pinnedOffset = pinnedInCycle ? 1 : 0;
	const optionCount = items.length + pinnedOffset;
	const lowestIndex = pinnedInCycle ? 0 : -1;
	const [highlightedIndex, setHighlightedIndex] = useState(lowestIndex);

	// Stable per-instance id base so each <li> gets a predictable id and the
	// input can set aria-activedescendant without a new public prop.
	const optionBaseId = useId();
	// Stable listbox id for aria-controls / id pairing.
	const listboxId = `${optionBaseId}-listbox`;

	// Rows can sit inside group wrappers, so locate them by id rather than as
	// direct children of the list.
	const { markKeyboardNav, markPointerNav, shouldHoverHighlight } =
		useKeyboardNavHighlight({
			listRef,
			highlightedIndex,
			resolveItem: (_list, index) => document.getElementById(`${optionBaseId}-option-${index}`),
		});

	// When the item set changes (search narrows/expands), the highlight follows
	// its row by id if that row is still listed, so Enter pressed as fresh results
	// land opens the row the user chose. It returns to the default only when the
	// row has gone or no item row was highlighted. Adjusted during render from a
	// snapshot of the previous ids, keyed on the id list rather than the array
	// reference so unrelated parent re-renders do not reset it mid-navigation.
	const itemIds = items.map((i) => i.id);
	const itemsSignature = `${pinnedOffset}:${itemIds.join(' ')}`;
	const [highlightSnapshot, setHighlightSnapshot] = useState({
		signature: itemsSignature,
		itemIds,
		pinnedOffset,
	});
	if (highlightSnapshot.signature !== itemsSignature) {
		const previousId = highlightSnapshot.itemIds[highlightedIndex - highlightSnapshot.pinnedOffset];
		const keptIndex = previousId === undefined ? -1 : itemIds.indexOf(previousId);
		setHighlightSnapshot({ signature: itemsSignature, itemIds, pinnedOffset });
		setHighlightedIndex(keptIndex === -1 ? lowestIndex : keptIndex + pinnedOffset);
	}

	const { isMobile, placement, top, bottom, left, width, maxHeight } = useResponsivePanelPlacement({
		triggerRef,
		panelRef: popoverRef,
		widthPx,
		anchorMode,
		align: panelAlign,
		gapPx: panelGapPx,
	});
	const hostedInput = !isMobile && searchInputHost !== undefined;

	// Focus trap for the mobile bottom-sheet (a modal surface). Inert on desktop.
	const sheetRef = useFocusTrap(isMobile);

	// Auto-focus the search input on mount.
	useEffect(() => {
		inputRef.current?.focus();
	}, [searchInputHost]);

	useBodyScrollLock(isMobile);

	// Escape closes the popover on both branches and hands focus back to the
	// trigger (an overlay trigger is `invisible` while open, so without this focus
	// would fall to <body>). Focus moves in the unmount cleanup because that is
	// the first moment the trigger is focusable again: the caller's re-render that
	// unmounts this popover is the one that drops `invisible`. A trigger that has
	// itself unmounted is skipped.
	const escapedRef = useRef(false);
	function focusTrigger() {
		triggerRef.current?.focus();
	}
	const focusTriggerRef = useRef(focusTrigger);
	focusTriggerRef.current = focusTrigger;
	useEffect(() => {
		function handleEscape(e: KeyboardEvent) {
			if (e.key !== 'Escape') return;
			escapedRef.current = true;
			onClose();
		}
		document.addEventListener('keydown', handleEscape);
		return () => {
			document.removeEventListener('keydown', handleEscape);
		};
	}, [onClose]);
	useEffect(() => {
		return () => {
			if (escapedRef.current) focusTriggerRef.current();
		};
	}, []);

	// Outside click is desktop-only: on mobile the dimmed backdrop owns
	// dismissal, so this listener is never registered there.
	useEffect(() => {
		if (isMobile) return;
		function handleClick(e: MouseEvent) {
			if (
				popoverRef.current &&
				!popoverRef.current.contains(e.target as Node) &&
				triggerRef.current &&
				!triggerRef.current.contains(e.target as Node) &&
				!searchInputHost?.contains(e.target as Node)
			) {
				onClose();
			}
		}
		document.addEventListener('mousedown', handleClick);
		return () => {
			document.removeEventListener('mousedown', handleClick);
		};
		// Deps are the destructured values this effect actually reads, not the
		// hook's returned object as a whole - see the "Returned object identity"
		// note at the top of `hooks/useResponsivePanelPlacement.ts` for why.
	}, [isMobile, onClose, triggerRef, searchInputHost]);

	// ---------------------------------------------------------------------------
	// Keyboard handler – mirrors Combobox.handleKeyDown exactly:
	//   ArrowDown: clamp to last (not wrap)
	//   ArrowUp:   move toward the first row (-1 = none; 0 when the pinned action
	//              is in the cycle, so a highlight is never lost). No wrap.
	//   Enter:     commit highlighted row, or first row when nothing highlighted,
	//              or no-op when there are no rows
	//   Escape:    owned by the document-level handler above; not handled here
	// ---------------------------------------------------------------------------
	function commitPinnedAction(action: SearchPopoverPinnedAction) {
		action.onSelect();
		if (!action.keepOpenOnSelect) onClose();
	}

	function commitItem(item: SearchPopoverItem) {
		onSelect(item.id);
		if (!item.keepOpenOnSelect) onClose();
	}

	function handleKeyDown(e: React.KeyboardEvent) {
		switch (e.key) {
			case 'ArrowDown':
				e.preventDefault();
				markKeyboardNav();
				setHighlightedIndex((prev) => (prev < optionCount - 1 ? prev + 1 : prev));
				break;
			case 'ArrowUp':
				e.preventDefault();
				markKeyboardNav();
				setHighlightedIndex((prev) => (prev > lowestIndex ? prev - 1 : lowestIndex));
				break;
			case 'Enter': {
				e.preventDefault();
				const optionIndex = highlightedIndex >= 0 && highlightedIndex < optionCount ? highlightedIndex : 0;
				if (optionIndex >= optionCount) break;
				if (pinnedAction && optionIndex < pinnedOffset) {
					commitPinnedAction(pinnedAction);
				} else {
					commitItem(items[optionIndex - pinnedOffset]);
				}
				break;
			}
		}
	}

	// ---------------------------------------------------------------------------
	// Shared search input (rendered once; reused in both branches below)
	// ---------------------------------------------------------------------------
	const activeDescendant =
		highlightedIndex >= 0 ? `${optionBaseId}-option-${highlightedIndex}` : undefined;

	const searchField = (
		<Input
			ref={inputRef}
			type="text"
			role="combobox"
			aria-expanded={true}
			aria-haspopup="listbox"
			aria-controls={listboxId}
			aria-autocomplete="list"
			aria-activedescendant={activeDescendant}
			value={searchValue}
			onChange={(e) => {
				markPointerNav();
				onSearchChange(e.target.value);
			}}
			onKeyDown={handleKeyDown}
			placeholder={placeholder}
			aria-label={inputAriaLabel}
			variant={hostedInput ? 'ghost' : 'default'}
			className={inputLeadingIcon && !hostedInput ? 'pl-9' : undefined}
		/>
	);
	const searchInput = hostedInput ? null : inputLeadingIcon ? (
		<div className="relative">
			<span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-primary">
				{inputLeadingIcon}
			</span>
			{searchField}
		</div>
	) : (
		searchField
	);

	const pinnedBlock = pinnedAction && !pinnedInCycle && (
		<div className="shrink-0 border-b border-border">
			<button
				type="button"
				onClick={() => commitPinnedAction(pinnedAction)}
				className="w-full px-3 py-2.5 text-left text-sm transition-colors hover:bg-primary/5"
			>
				<span className="block truncate font-medium text-primary">{pinnedAction.label}</span>
				{pinnedAction.sublabel && (
					<span className="block truncate text-xs text-text-muted">{pinnedAction.sublabel}</span>
				)}
			</button>
		</div>
	);

	// ---------------------------------------------------------------------------
	// Result rows
	// ---------------------------------------------------------------------------
	// aria-selected on each <li role="option"> tracks the COMMITTED selection
	// (selectedId) – this is the ARIA combobox+listbox pattern: aria-selected
	// reflects the chosen value, while aria-activedescendant on the input alone
	// signals the currently highlighted (focused) row. The two states are
	// visually distinct: committed selection uses bg-primary/5, the keyboard-
	// highlighted row uses bg-bg-subtle.
	const emphasise = (text: string, plainClass: string) => {
		const segments = splitOnMatch(text, searchValue);
		if (!segments.some((segment) => segment.matched)) return <span className={plainClass}>{text}</span>;
		let offset = 0;
		return segments.map((segment) => {
			const key = offset;
			offset += segment.text.length;
			return (
				<span key={key} className={segment.matched ? 'font-semibold text-secondary' : plainClass}>
					{segment.text}
				</span>
			);
		});
	};

	const renderRichItemRow = (item: SearchPopoverItem, index: number) => {
		const isHighlighted = index === highlightedIndex;
		const isSelected = item.id === selectedId;
		const hasMatch = splitOnMatch(item.label, searchValue).some((segment) => segment.matched);
		return (
			<li
				key={item.id}
				id={`${optionBaseId}-option-${index}`}
				role="option"
				aria-selected={isSelected}
				onMouseEnter={() => {
					if (shouldHoverHighlight()) setHighlightedIndex(index);
				}}
			>
				<button
					type="button"
					onClick={() => commitItem(item)}
					className={[
						'relative flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left text-sm transition-colors',
						isHighlighted ? 'bg-bg-muted' : isSelected ? 'bg-primary/5' : '',
					].join(' ')}
				>
					{isHighlighted && (
						<span aria-hidden="true" className="absolute inset-y-2 left-0 w-0.5 rounded-sm bg-primary" />
					)}
					<span
						className={[
							'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors',
							isHighlighted ? 'bg-bg-primary text-primary ring-1 ring-border' : 'bg-bg-muted text-text-muted',
						].join(' ')}
					>
						{item.icon}
					</span>
					<span className="block min-w-0 flex-1">
						<span className="block truncate">
							{emphasise(item.label, hasMatch ? 'text-text-muted' : 'font-medium text-secondary')}
						</span>
						{item.sublabel && (
							<span className="block truncate text-xs text-text-muted">
								{emphasise(item.sublabel, 'text-text-muted')}
							</span>
						)}
					</span>
					{item.trailing ? (
						<span className="flex shrink-0 flex-col items-end gap-0.5">{item.trailing}</span>
					) : (
						isHighlighted &&
						!isMobile && (
							<kbd
								aria-hidden="true"
								className="shrink-0 rounded border border-border bg-bg-primary px-1.5 py-0.5 font-sans text-xs text-text-muted"
							>
								{'\u21b5'}
							</kbd>
						)
					)}
				</button>
			</li>
		);
	};

	const renderItemRow = (item: SearchPopoverItem, index: number) => {
		if (rich) return renderRichItemRow(item, index);
		const isHighlighted = index === highlightedIndex;
		const isSelected = item.id === selectedId;
		const text = (
			<>
				<span className="block truncate">{item.label}</span>
				{item.sublabel && (
					<span className="block truncate text-xs text-text-muted">{item.sublabel}</span>
				)}
			</>
		);
		return (
			<li
				key={item.id}
				id={`${optionBaseId}-option-${index}`}
				role="option"
				aria-selected={isSelected}
				onMouseEnter={() => {
					if (shouldHoverHighlight()) setHighlightedIndex(index);
				}}
			>
				<button
					type="button"
					onClick={() => commitItem(item)}
					className={[
						'w-full px-3 py-2.5 text-left text-sm transition-colors',
						item.icon ? 'flex items-center gap-2.5' : '',
						isSelected
							? 'bg-primary/5 font-medium text-primary'
							: isHighlighted
								? 'bg-bg-subtle text-secondary'
								: 'text-secondary hover:bg-bg-subtle',
					].join(' ')}
				>
					{item.icon ? (
						<>
							{item.icon}
							<span className="block min-w-0 flex-1">{text}</span>
						</>
					) : (
						text
					)}
				</button>
			</li>
		);
	};

	// Consecutive items sharing a `group` form one run rendered under a heading;
	// ungrouped items render flat, exactly as before. `index` is the option index
	// (items are offset by the pinned option when it is in the keyboard cycle).
	const runs: { group: string | undefined; entries: { item: SearchPopoverItem; index: number }[] }[] = [];
	items.forEach((item, i) => {
		const last = runs[runs.length - 1];
		const entry = { item, index: i + pinnedOffset };
		if (last && last.group === item.group) last.entries.push(entry);
		else runs.push({ group: item.group, entries: [entry] });
	});

	const itemRows = runs.map((run, runIndex) => {
		if (run.group === undefined) return run.entries.map(({ item, index }) => renderItemRow(item, index));
		const headingId = `${optionBaseId}-group-${runIndex}`;
		return (
			<li key={`group-${run.entries[0].item.id}`} role="presentation">
				<div
					id={headingId}
					className={`px-3 pb-1 text-xs font-medium text-text-muted ${rich ? 'pt-3' : 'pt-2'}`}
				>
					{run.group}
				</div>
				<ul role="group" aria-labelledby={headingId}>
					{run.entries.map(({ item, index }) => renderItemRow(item, index))}
				</ul>
			</li>
		);
	});

	const pinnedOption = pinnedAction && pinnedInCycle && (
		<li
			id={`${optionBaseId}-option-0`}
			role="option"
			aria-selected={false}
			onMouseEnter={() => {
				if (shouldHoverHighlight()) setHighlightedIndex(0);
			}}
			className={rich ? undefined : 'border-b border-border'}
		>
			{rich ? (
				<button
					type="button"
					onClick={() => commitPinnedAction(pinnedAction)}
					className={[
						'flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left text-sm transition-colors',
						highlightedIndex === 0 ? 'bg-primary/10' : 'bg-primary/5',
					].join(' ')}
				>
					<span
						className={[
							'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors',
							highlightedIndex === 0 ? 'bg-primary text-white' : 'bg-primary/10 text-primary',
						].join(' ')}
					>
						{pinnedAction.icon}
					</span>
					<span className="block min-w-0 flex-1">
						<span className="block truncate font-medium text-primary">{pinnedAction.label}</span>
						{pinnedAction.sublabel && (
							<span className="block truncate text-xs text-text-muted">{pinnedAction.sublabel}</span>
						)}
					</span>
					{highlightedIndex === 0 && !isMobile && pinnedAction.enterHint && (
						<span className="flex shrink-0 items-center gap-1 text-xs font-medium text-primary">
							<kbd
								aria-hidden="true"
								className="rounded border border-primary/30 bg-bg-primary px-1.5 py-0.5 font-sans"
							>
								{'\u21b5'}
							</kbd>
							{pinnedAction.enterHint}
						</span>
					)}
				</button>
			) : (
				<button
					type="button"
					onClick={() => commitPinnedAction(pinnedAction)}
					className={[
						'w-full px-3 py-2.5 text-left text-sm transition-colors',
						highlightedIndex === 0 ? 'bg-primary/5' : 'hover:bg-primary/5',
					].join(' ')}
				>
					<span className="block truncate font-medium text-primary">{pinnedAction.label}</span>
					{pinnedAction.sublabel && (
						<span className="block truncate text-xs text-text-muted">{pinnedAction.sublabel}</span>
					)}
				</button>
			)}
		</li>
	);

	const listItems =
		isLoading && items.length === 0 ? (
			<li className="px-3 py-2 text-sm text-text-light" role="option" aria-selected={false}>
				Loading...
			</li>
		) : items.length === 0 ? (
			<li className="px-3 py-4 text-center text-sm text-text-muted">{emptyLabel}</li>
		) : (
			itemRows
		);

	const loadMore = hasMore && onLoadMore && (
		<li>
			<button
				type="button"
				onClick={onLoadMore}
				disabled={isLoadingMore}
				className="w-full px-3 py-2 text-center text-xs text-text-muted hover:bg-bg-muted disabled:opacity-50"
			>
				{isLoadingMore ? 'Loading...' : 'Load more'}
			</button>
		</li>
	);

	if (isMobile) {
		return createPortal(
			<div className="fixed inset-0 z-[9999]">
				<div
					className="animate-fade-in absolute inset-0 bg-black/40"
					aria-hidden="true"
					onClick={onClose}
				/>
				<div
					ref={sheetRef}
					data-popover-panel
					role="dialog"
					aria-modal="true"
					style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
					className="animate-fade-up absolute inset-x-0 bottom-0 flex max-h-[80vh] flex-col rounded-t-2xl border-t border-border bg-bg-primary shadow-lg"
				>
					<div className="mx-auto mt-2 h-1 w-9 shrink-0 rounded-full bg-border" aria-hidden="true" />
					<div className="shrink-0 p-3 pb-2">{searchInput}</div>
					{pinnedBlock}
					<ul
						ref={listRef}
						id={listboxId}
						className={`min-h-0 flex-1 overflow-y-auto ${rich ? 'p-1.5 pb-4' : 'pb-1'}`}
						role="listbox"
						aria-label={listAriaLabel}
						onMouseMove={markPointerNav}
					>
						{pinnedOption}
						{listItems}
						{loadMore}
					</ul>
				</div>
			</div>,
			document.body,
		);
	}

	const anchoredStyle: CSSProperties = {
		top,
		bottom,
		left,
		maxHeight,
		...(anchorMode === 'overlay' && width > 0 ? { width } : {}),
	};
	const panelShapeClass = rich ? 'animate-fade-in rounded-xl shadow-xl' : 'rounded-lg shadow-lg';
	const sizingClass = anchorMode === 'overlay' ? '' : widthClass;

	return createPortal(
		<>
			{scrimTopPx !== undefined && (
				<div
					aria-hidden="true"
					style={{ top: scrimTopPx }}
					className="animate-fade-in fixed inset-x-0 bottom-0 z-[9998] bg-black/10"
				/>
			)}
			<div
				ref={popoverRef}
				data-popover-panel
				data-placement={placement}
				style={anchoredStyle}
				className={`fixed z-[9999] flex flex-col overflow-hidden ${sizingClass} ${panelShapeClass} border border-border bg-bg-primary`}
			>
				{searchInput && <div className="shrink-0">{searchInput}</div>}
				{pinnedBlock}
				<ul
					ref={listRef}
					id={listboxId}
					className={`min-h-0 ${listMaxHeightClass} overflow-y-auto ${rich ? 'p-1.5' : ''}`}
					role="listbox"
					aria-label={listAriaLabel}
					onMouseMove={markPointerNav}
				>
					{pinnedOption}
					{listItems}
					{loadMore}
				</ul>
			</div>
			{hostedInput && searchInputHost && createPortal(searchField, searchInputHost)}
		</>,
		document.body,
	);
}
